import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Pricing.css';
import Footer from '../components/home/Footer';

function Pricing() {
  const navigate = useNavigate();
  const plans = [
    {
      level: 1,
      name: 'Level 1',
      price: 'Free',
      description: 'Start discovering co-founders and opportunities',
      features: [
        'Basic profile creation',
        'Search by name, age, and gender',
        'Filter by technical background',
        'Filter by startup timing',
        'Filter by idea status',
        'Community feed access',
        'Up to 25 search results per page',
        'Post 1 time per day'
      ],
      cta: 'Get Started',
      badge: 'Free on signup'
    },
    {
      level: 25,
      name: 'Level 25',
      price: '$25',
      description: 'Access location and preferences',
      features: [
        'Everything in Level 1',
        'Filter by location',
        'Search by about me',
        'Search by impressive achievements',
        'Filter by Y Combinator status',
        'Filter by interests',
        'Filter by responsibilities',
        'Current co-founder preference filters',
        'Up to 25 search results per page',
        'Post 1 time per day'
      ],
      cta: 'Upgrade Now'
    },
    {
      level: 50,
      name: 'Level 50',
      price: '$50',
      description: 'Full company and background details',
      features: [
        'Everything in Level 25',
        'Search by education',
        'Company and equity filters',
        'Ideal co-founder preference filters',
        'Up to 50 search results per page',
        'Post 5 times per day'
      ],
      cta: 'Upgrade Now'
    },
    {
      level: 100,
      name: 'Level 100',
      price: '$100',
      description: 'Full access to all search filters',
      features: [
        'Everything in Level 50',
        'Employment and company detail search',
        'Funding status and potential ideas',
        'Ideal co-founder text search',
        'Up to 100 search results per page',
        'Post 10 times per day',
        'Priority support'
      ],
      cta: 'Upgrade Now'
    },
    {
      level: 5000,
      name: 'MVP Development',
      price: '$5,000',
      description: 'Optional build support for startups ready to ship',
      features: [
        'Full stack MVP development',
        'AI integration when needed',
        'Database design and setup',
        'Deployment and hosting setup',
        'Technical documentation',
        'Post-launch support',
        'Delivery: 15-30 days'
      ],
      cta: 'Get Started'
    },
    {
      level: 10000,
      name: 'Advanced MVP',
      price: '$10,000',
      description: 'Advanced build support for complex products',
      features: [
        'Everything in MVP Development',
        'Advanced architecture design',
        'Enhanced security features',
        'Extended post-launch support',
        'Delivery: 15-30 days'
      ],
      cta: 'Get Started'
    }
  ];

  const jobPlans = [
    {
      id: 'basic',
      name: 'Basic Job Posting',
      price: 'Free',
      description: 'Post an open role to attract candidates',
      features: [
        'Standard job listing',
        'Reach candidates on CofounderHubs',
        'Suitable for straightforward hiring needs'
      ],
      cta: 'Post a Job',
      badge: 'Free'
    },
    {
      id: 'enhanced',
      name: 'Enhanced Job Posting',
      price: '$29',
      description: 'Greater visibility for your open position',
      features: [
        'Everything in Basic',
        'Enhanced listing placement',
        'Better visibility to qualified candidates'
      ],
      cta: 'Post a Job',
      badge: 'Popular'
    },
    {
      id: 'premium',
      name: 'Premium Job Posting',
      price: '$99',
      description: 'Maximum visibility for priority roles',
      features: [
        'Everything in Enhanced',
        'Premium listing placement',
        'Priority visibility for open roles'
      ],
      cta: 'Post a Job'
    }
  ];

  return (
    <div className="pricing">
      <div className="pricing-header">
        <h1>Simple, Transparent Pricing</h1>
        <p>Plans for co-founder discovery, job posting, and optional build support</p>
      </div>

      <div className="pricing-container">
        <div className="pricing-header" style={{ paddingBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.5rem', margin: 0 }}>Platform Access</h2>
          <p style={{ marginTop: '0.5rem' }}>Unlock search filters and community features for co-founder and talent discovery</p>
        </div>
        <div className="pricing-grid">
          {plans.map((plan, index) => (
            <div key={index} className="pricing-card">
              <div className="pricing-card-header">
                <div>
                  <h2>{plan.name}</h2>
                </div>
                {plan.badge && <span className="pricing-badge">{plan.badge}</span>}
              </div>
              <div className="pricing-price">
                <span className="price">{plan.price}</span>
                <span className="one-time">one-time</span>
              </div>
              <p className="pricing-description">{plan.description}</p>

              <ul className="pricing-features">
                {plan.features.map((feature, idx) => (
                  <li key={idx}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button 
                className="pricing-cta secondary"
                onClick={() => {
                  if (plan.level === 1) {
                    navigate('/search');
                  } else if (plan.level === 5000 || plan.level === 10000) {
                    navigate('/contact');
                  } else {
                    navigate(`/payment?level=${plan.level}`);
                  }
                }}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-container">
        <div className="pricing-header" style={{ paddingBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.5rem', margin: 0 }}>Job Posting Plans</h2>
          <p style={{ marginTop: '0.5rem' }}>Companies can post hiring positions to attract qualified candidates. Need help finding talent? Contact us about recruitment support.</p>
        </div>
        <div className="pricing-grid">
          {jobPlans.map((plan, index) => (
            <div key={index} className="pricing-card">
              <div className="pricing-card-header">
                <div>
                  <h2>{plan.name}</h2>
                </div>
                {plan.badge && <span className="pricing-badge">{plan.badge}</span>}
              </div>
              <div className="pricing-price">
                <span className="price">{plan.price}</span>
                <span className="one-time">per posting</span>
              </div>
              <p className="pricing-description">{plan.description}</p>

              <ul className="pricing-features">
                {plan.features.map((feature, idx) => (
                  <li key={idx}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button 
                className="pricing-cta secondary"
                onClick={() => navigate(`/post-job?plan=${plan.id}`)}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h3>What are levels?</h3>
            <p>Levels unlock additional search filters and features for discovering co-founders, candidates, and related opportunities on the platform.</p>
          </div>
          <div className="faq-item">
            <h3>Is this a one-time payment?</h3>
            <p>Yes! Each level upgrade is a one-time payment. Once purchased, you have permanent access to all filters at that level.</p>
          </div>
          <div className="faq-item">
            <h3>How do job posting plans work?</h3>
            <p>Companies can post open roles with Basic (Free), Enhanced ($29), or Premium ($99) plans. For help identifying and screening candidates, contact us about recruitment support.</p>
          </div>
          <div className="faq-item">
            <h3>Can I upgrade to a higher level later?</h3>
            <p>Absolutely! You can upgrade to any higher level at any time. You'll only pay the difference between your current level and the new level.</p>
          </div>
          <div className="faq-item">
            <h3>What if I'm not satisfied?</h3>
            <p>We offer a 30-day money-back guarantee if you're not satisfied with your level upgrade.</p>
          </div>
          <div className="faq-item">
            <h3>Do I need to upgrade?</h3>
            <p>No! The free level provides basic search and filtering. Upgrades and job postings are optional based on your needs.</p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Pricing;
